/* Culture v25. Native Shopify cart POST with bounded, app-safe delegated controls. */
(() => {
  const initProduct = root => {
    if (root.dataset.nhReady === 'true') return;

    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    let product;
    let config;

    try {
      product = JSON.parse($('[data-nh-product-json]').textContent);
      config = JSON.parse($('[data-nh-plan-json]').textContent);
      if (!Array.isArray(product.variants) || !product.variants.length) return;
    } catch (_) {
      return; // Shopify's native select and number input remain usable without JavaScript.
    }

    const formatter = new Intl.NumberFormat(config.locale || 'en', {
      style: 'currency',
      currency: config.currency || 'USD'
    });
    const money = value => formatter.format(Number(value) / 100);
    const form = () => $('form[action*="/cart/add"]') || $('form');
    const optionGroups = () => $$('[data-nh-option-group]');
    const quantityField = () => $('[data-nh-quantity-input]');

    let variant = null;
    let allocation = null;
    let activePlan = '';
    let purchasable = false;
    let busy = false;
    let quantity = Number(quantityField()?.value) || 1;
    let rule = {};
    let min = 1;
    let max = Infinity;
    let step = 1;
    let appSyncComplete = false;
    const appSyncTimers = [];
    const boundForms = new WeakSet();

    const message = text => {
      const target = $('[data-nh-form-message]');
      if (target) target.textContent = text;
    };

    const externalPlanControls = () => {
      const currentForm = form();
      const nativePlan = $('[data-nh-selling-plan]');
      if (!currentForm) return [];
      return [...currentForm.querySelectorAll('[name="selling_plan"]')]
        .filter(control => control !== nativePlan && !control.disabled);
    };

    const getPlan = () => {
      const nativePlan = $('[data-nh-selling-plan]');
      const nativeWrapper = $('[data-nh-plan-wrapper]');
      const controls = externalPlanControls();

      if (nativePlan) nativePlan.disabled = controls.length > 0;
      if (nativeWrapper) nativeWrapper.hidden = controls.length > 0;
      if (!controls.length) return nativePlan?.value || '';

      const checked = controls.find(control => ['radio', 'checkbox'].includes(control.type) && control.checked);
      const nonChoice = controls.find(control => !['radio', 'checkbox', 'hidden'].includes(control.type));
      const hiddenPlan = controls.find(control => control.type === 'hidden' && control.value);
      return checked?.value ?? nonChoice?.value ?? hiddenPlan?.value ?? '';
    };

    const showMedia = id => {
      const media = $$('[data-nh-media-id]');
      if (!media.some(panel => panel.dataset.nhMediaId === String(id))) return;

      media.forEach(panel => {
        const active = panel.dataset.nhMediaId === String(id);
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (active) panel.querySelector('img')?.setAttribute('loading', 'eager');
        if (!active) {
          panel.querySelectorAll('video').forEach(video => video.pause());
          panel.querySelectorAll('iframe').forEach(frame => {
            if (frame.src) {
              frame.dataset.nhVideoSrc = frame.src;
              frame.removeAttribute('src');
            }
          });
        } else {
          panel.querySelectorAll('iframe[data-nh-video-src]').forEach(frame => {
            if (!frame.getAttribute('src')) frame.src = frame.dataset.nhVideoSrc;
          });
        }
      });

      $$('[data-nh-thumb]').forEach(thumb => {
        const active = thumb.dataset.nhThumb === String(id);
        thumb.setAttribute('aria-selected', String(active));
        thumb.tabIndex = active ? 0 : -1;
      });
    };

    const normalizeQuantity = value => {
      const number = Number(value);
      const safe = Number.isFinite(number) ? Math.max(min, number) : min;
      const normalized = min + Math.floor((safe - min) / step) * step;
      return Number.isFinite(max) ? Math.max(min, Math.min(normalized, max)) : Math.max(min, normalized);
    };

    // Keep the primary action compact. Pre-order context lives beside the form,
    // so the CTA remains the same familiar conversion action on every product.
    const orderLabel = () => 'Add to cart';

    const paint = () => {
      const liveQuantity = quantityField();
      const add = $('[data-nh-add-to-cart]');
      const mobileAdd = $('[data-nh-mobile-add]');
      const minus = $('[data-nh-quantity-minus]');
      const plus = $('[data-nh-quantity-plus]');
      let price = allocation?.price ?? variant?.price ?? 0;

      if (!allocation) {
        const tier = (rule.priceBreaks || [])
          .filter(item => item.minimum_quantity <= quantity + (rule.inCart || 0))
          .sort((a, b) => b.minimum_quantity - a.minimum_quantity)[0];
        if (tier) price = tier.price;
      }

      const validQuantity = Boolean(liveQuantity) && liveQuantity.value !== '' && Number(liveQuantity.value) === quantity && quantity <= max;
      const enabled = purchasable && validQuantity && !busy;
      const unavailable = !variant ? 'Choose valid options' : !variant.available ? 'Sold out' : max < min ? 'Limit reached' : 'Check purchase option';
      const label = busy ? 'Adding…' : purchasable ? orderLabel() : unavailable;

      if (add) {
        add.disabled = !enabled;
        add.setAttribute('aria-busy', String(busy));
        const addLabel = add.querySelector('[data-nh-add-label]');
        if (addLabel) addLabel.textContent = label;
        else add.textContent = `${label} →`;
      }
      if (mobileAdd) {
        mobileAdd.disabled = !enabled;
        mobileAdd.textContent = `${label} →`;
      }
      if (minus) minus.disabled = busy || quantity <= min;
      if (plus) plus.disabled = busy || quantity + step > max;

      $$('[data-nh-price]').forEach(element => { element.textContent = variant ? money(price) : '—'; });
      $$('[data-nh-total],[data-nh-mobile-price]').forEach(element => {
        element.textContent = variant && validQuantity ? money(price * quantity) : '—';
      });

      const compare = $('[data-nh-compare-price]');
      if (compare) {
        compare.hidden = !(variant?.compare_at_price > price);
        compare.textContent = compare.hidden ? '' : money(variant.compare_at_price);
      }

      const stock = $('[data-nh-stock]');
      if (stock) {
        stock.textContent = !variant
          ? 'This combination is unavailable. Choose another option.'
          : !variant.available
            ? 'Currently sold out'
            : max < min
              ? 'The available quantity is already in your cart.'
              : config.preorder
                ? 'Pre-order. Check delivery details before ordering.'
                : rule.managed && rule.policy === 'continue' && rule.inventory <= 0
                  ? 'Available to order. Shipping timing may vary.'
                  : 'Available to order';
      }

      const help = $('[data-nh-quantity-help]');
      if (help) {
        const unit = config.kind === 'hydration'
          ? `${quantity} ${quantity === 1 ? 'case' : 'cases'} · ${quantity * 12} cans.`
          : `${quantity} ${quantity === 1 ? 'item' : 'items'}.`;
        help.textContent = unit
          + (min > 1 ? ` Minimum ${min}.` : '')
          + (step > 1 ? ` Increments of ${step}.` : '')
          + (Number.isFinite(max) ? ` Maximum available to add: ${Math.max(0, max)}.` : '');
      }
    };

    const bindForm = currentForm => {
      if (!currentForm || boundForms.has(currentForm)) return;
      currentForm.addEventListener('formdata', event => {
        if (!variant || !purchasable) return;
        event.formData.set('id', String(variant.id));
        event.formData.set('quantity', String(quantity));
        if (activePlan) event.formData.set('selling_plan', activePlan);
        else event.formData.delete('selling_plan');
      });
      boundForms.add(currentForm);
    };

    const sync = (updateUrl = false, changeImage = true) => {
      bindForm(form());

      const groups = optionGroups();
      const values = groups.map(group => group.querySelector('.is-selected')?.dataset.nhOptionValue);
      variant = groups.length
        ? product.variants.find(item => item.options.every((value, index) => value === values[index]))
        : product.variants[0];

      rule = config.rules?.[String(variant?.id)] || {};
      min = Math.max(1, Number(rule.min) || 1);
      step = Math.max(1, Number(rule.step) || 1);
      max = rule.max == null ? Infinity : Math.max(0, Number(rule.max) - (Number(rule.inCart) || 0));
      if (rule.managed && rule.policy === 'deny') {
        max = Math.min(max, Math.max(0, Number(rule.inventory) - (Number(rule.inCart) || 0)));
      }
      if (Number.isFinite(max) && max >= min) max = min + Math.floor((max - min) / step) * step;

      const liveQuantity = quantityField();
      quantity = normalizeQuantity(liveQuantity?.value || quantity || min);
      if (liveQuantity) {
        liveQuantity.value = quantity;
        liveQuantity.min = min;
        liveQuantity.step = step;
        if (Number.isFinite(max)) liveQuantity.max = Math.max(min, max);
        else liveQuantity.removeAttribute('max');
      }

      const nativePlan = $('[data-nh-selling-plan]');
      const allocations = config.allocations?.[String(variant?.id)] || [];
      if (nativePlan && !externalPlanControls().length) {
        [...nativePlan.options].forEach(option => {
          option.disabled = Boolean(option.value) && !allocations.some(item => String(item.selling_plan?.id) === option.value);
        });
        if (nativePlan.selectedOptions[0]?.disabled) {
          nativePlan.value = [...nativePlan.options].find(option => !option.disabled)?.value || '';
        }
      }

      activePlan = getPlan();
      allocation = allocations.find(item => String(item.selling_plan?.id) === activePlan);
      purchasable = Boolean(variant?.available)
        && max >= min
        && (!activePlan || Boolean(allocation))
        && (!product.requires_selling_plan || Boolean(allocation));

      const variantInput = $('[data-nh-variant-input]');
      const nativeVariant = $('[data-nh-native-variant]');
      if (variant) {
        if (variantInput) {
          variantInput.disabled = false;
          variantInput.value = variant.id;
        }
        if (nativeVariant) {
          nativeVariant.value = variant.id;
          nativeVariant.disabled = true;
        }
        const mobileVariant = $('[data-nh-mobile-variant]');
        if (mobileVariant) mobileVariant.textContent = variant.title === 'Default Title' ? product.title : variant.title;
        if (changeImage) {
          showMedia(rule.mediaId || variant.featured_media?.id || variant.featured_image?.id || config.featuredMediaId || product.featured_media?.id || product.media?.[0]?.id);
        }
      } else {
        if (variantInput) variantInput.disabled = true;
        const mobileVariant = $('[data-nh-mobile-variant]');
        if (mobileVariant) mobileVariant.textContent = 'Choose valid options';
      }

      paint();
      if (updateUrl && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        if (activePlan) url.searchParams.set('selling_plan', activePlan);
        else url.searchParams.delete('selling_plan');
        window.history.replaceState({}, '', url);
      }
    };

    const setQuantity = value => {
      quantity = normalizeQuantity(value);
      const liveQuantity = quantityField();
      if (liveQuantity) liveQuantity.value = quantity;
      paint();
    };

    root.addEventListener('click', event => {
      const option = event.target.closest('[data-nh-option-value]');
      if (option && root.contains(option)) {
        event.preventDefault();
        const group = option.closest('[data-nh-option-group]');
        group.querySelectorAll('[data-nh-option-value]').forEach(element => {
          const selected = element === option;
          element.classList.toggle('is-selected', selected);
          element.setAttribute('aria-pressed', String(selected));
        });
        const label = group.querySelector('[data-nh-option-label]');
        if (label) label.textContent = option.dataset.nhOptionValue;
        message('');
        sync(true);
        $('[data-nh-variant-input]')?.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const thumb = event.target.closest('[data-nh-thumb]');
      if (thumb && root.contains(thumb)) {
        event.preventDefault();
        showMedia(thumb.dataset.nhThumb);
        return;
      }

      const liveQuantity = quantityField();
      if (event.target.closest('[data-nh-quantity-minus]')) {
        event.preventDefault();
        setQuantity(Number(liveQuantity?.value || quantity) - step);
      } else if (event.target.closest('[data-nh-quantity-plus]')) {
        event.preventDefault();
        setQuantity(Number(liveQuantity?.value || quantity) + step);
      }
    });

    root.addEventListener('keydown', event => {
      const thumb = event.target.closest('[data-nh-thumb]');
      if (!thumb || !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      const thumbs = $$('[data-nh-thumb]');
      const index = thumbs.indexOf(thumb);
      if (index < 0) return;
      event.preventDefault();
      const next = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? thumbs.length - 1
          : (index + (event.key === 'ArrowRight' ? 1 : -1) + thumbs.length) % thumbs.length;
      thumbs[next].focus();
      showMedia(thumbs[next].dataset.nhThumb);
    });

    root.addEventListener('input', event => {
      if (!event.target.matches('[data-nh-quantity-input]')) return;
      quantity = normalizeQuantity(event.target.value);
      paint();
    });

    root.addEventListener('change', event => {
      if (event.target.matches('[data-nh-quantity-input]')) {
        setQuantity(event.target.value);
      } else if (event.target.matches('[name="selling_plan"],[data-nh-selling-plan]')) {
        sync(true, false);
      }
    });

    root.addEventListener('submit', event => {
      if (!event.target.matches('form[action*="/cart/add"]')) return;
      if (busy) {
        event.preventDefault();
        return;
      }

      setQuantity(quantityField()?.value || quantity);
      sync(false, false);
      if (!purchasable) {
        event.preventDefault();
        message('Check your selected options and quantity before adding to cart.');
        return;
      }

      message('Adding to cart…');
      queueMicrotask(() => {
        if (!event.defaultPrevented) {
          busy = true;
          paint();
          window.setTimeout(() => {
            busy = false;
            paint();
          }, 5000);
        }
      });
    }, true);

    const requestedPlan = new URL(window.location.href).searchParams.get('selling_plan');
    const nativePlan = $('[data-nh-selling-plan]');
    if (requestedPlan && nativePlan && [...nativePlan.options].some(option => option.value === requestedPlan)) {
      nativePlan.value = requestedPlan;
    }

    window.addEventListener('pageshow', () => {
      busy = false;
      message('');
      sync(false, false);
    });

    /*
      Subscription apps can rebuild their own controls several times during
      startup. A recurring DOM observer can create a feedback loop with those
      redraws. These bounded checks synchronize at most five times and stop as
      soon as an external selling-plan control is ready. All customer actions
      remain delegated to the product root, so later app replacements work
      without watching or moving app-owned DOM.
    */
    [300, 900, 1800, 3200, 5000].forEach(delay => {
      appSyncTimers.push(window.setTimeout(() => {
        if (appSyncComplete || !root.isConnected) return;
        const appWidget = $('.appstle_sub_widget');
        const externalPlans = externalPlanControls();
        if (!appWidget && !externalPlans.length) return;
        sync(false, false);
        if (externalPlans.length) appSyncComplete = true;
      }, delay));
    });

    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      appSyncTimers.forEach(timer => window.clearTimeout(timer));
    });

    root.dataset.nhReady = 'true';
    root.dataset.nhRelease = config.release || 'culture-v26';
    document.body.classList.add('nh-product-page');
    sync();
  };

  const init = context => context.querySelectorAll('[data-nh-preview-product]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
